import type { PaymentResponseDto } from '@trellis/api'
import { Button } from '@trellis/roots/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@trellis/roots/sheet'
import { Skeleton } from '@trellis/roots/skeleton'
import { Typography } from '@trellis/roots/typography'
import { Download, FileText, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'

import { usePaymentReceiptQuery } from '@/hooks'
import { formatCurrency, formatDate } from '@/lib/format'

const IMAGE_MIME = new Set(['image/jpeg', 'image/png', 'image/webp'])

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  STRIPE_CARD: 'Card',
  STRIPE_OTHER: 'Stripe',
  MANUAL: 'Manual',
}

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-0.5">
      <Typography variant="label" color="muted">
        {label}
      </Typography>
      <div className="text-sm">{children}</div>
    </div>
  )
}

function ReceiptPreview({
  invoiceId,
  paymentId,
  hasReceipt,
  mimeType,
  filename,
}: {
  invoiceId: string
  paymentId: string
  hasReceipt: boolean
  mimeType: string | null
  filename: string | null
}) {
  const receiptQuery = usePaymentReceiptQuery(invoiceId, paymentId, hasReceipt)
  const receiptUrl =
    receiptQuery.data?.data?.receiptUrls?.xl ??
    receiptQuery.data?.data?.receiptUrls?.sm ??
    null

  if (!hasReceipt) {
    return <span className="text-muted-foreground">No receipt</span>
  }

  if (receiptQuery.isLoading || !receiptUrl) {
    return <Skeleton className="h-40 w-full rounded-lg" />
  }

  if (mimeType && IMAGE_MIME.has(mimeType)) {
    return (
      <img
        src={receiptUrl}
        alt="Receipt"
        className="max-h-64 w-full rounded-md border object-contain"
      />
    )
  }

  if (mimeType === 'application/pdf') {
    return (
      <iframe
        src={receiptUrl}
        className="h-64 w-full rounded-lg border-0"
        title="Receipt PDF"
      />
    )
  }

  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border p-4 text-center">
      <FileText className="text-muted-foreground size-8" />
      <Typography variant="body-sm" color="muted">
        {filename ?? 'Receipt'}
      </Typography>
      <a
        href={receiptUrl}
        download={filename ?? undefined}
        className="text-primary inline-flex items-center gap-1 text-sm underline underline-offset-2"
      >
        <Download className="size-3.5" />
        Download receipt
      </a>
    </div>
  )
}

interface PaymentDetailSheetProps {
  invoiceId: string
  payment: PaymentResponseDto | null
  currency: string
  onOpenChange: (isOpen: boolean) => void
  onRemove: (paymentId: string) => void
}

export function PaymentDetailSheet({
  invoiceId,
  payment,
  currency,
  onOpenChange,
  onRemove,
}: PaymentDetailSheetProps) {
  return (
    <Sheet open={payment !== null} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[min(90vw,480px)] max-w-none overflow-y-auto sm:max-w-none"
      >
        <SheetHeader>
          <SheetTitle>
            {payment ? formatCurrency(Number(payment.amount), currency) : 'Payment'}
          </SheetTitle>
        </SheetHeader>

        {payment && (
          <div className="mt-6 space-y-5 p-4">
            <SummaryRow label="Date">
              {formatDate(payment.paidAt, { dateStyle: 'medium', timeStyle: 'short' })}
            </SummaryRow>

            <SummaryRow label="Method">
              {PAYMENT_METHOD_LABELS[payment.method] ?? payment.method}
            </SummaryRow>

            {payment.recordedByName && (
              <SummaryRow label="Recorded by">{payment.recordedByName}</SummaryRow>
            )}

            {payment.note && (
              <SummaryRow label="Note">
                <p className="italic">"{payment.note}"</p>
              </SummaryRow>
            )}

            <SummaryRow label="Receipt">
              <div className="mt-1">
                <ReceiptPreview
                  invoiceId={invoiceId}
                  paymentId={payment.id}
                  hasReceipt={payment.hasReceipt}
                  mimeType={payment.receiptMimeType}
                  filename={payment.receiptFilename}
                />
              </div>
            </SummaryRow>

            {payment.method === 'MANUAL' && (
              <Button
                variant="outline"
                className="text-destructive-text hover:text-destructive-text w-full"
                onClick={() => onRemove(payment.id)}
              >
                <Trash2 className="size-3.5" />
                Remove payment
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
